import { useEffect } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Prezzo({ item }) {
  if (item.price_type === "on_request") {
    return (
      <span className="shrink-0 text-[0.62rem] uppercase tracking-[0.16em] text-sage">
        Su prenotazione
      </span>
    );
  }
  if (item.price === null || item.price === undefined) return null;
  return (
    <span className="shrink-0 font-serif text-lg text-wood/75 tabular-nums">
      € {formatPrice(item.price)}
      {item.price_type === "per_hg" && <span className="text-sm text-wood/50"> / hg</span>}
    </span>
  );
}

export default function CartaQR() {
  const { token } = useParams();
  const { settings } = useSettings();
  const { data, error, isLoading } = useSWR(`/carta/${token}`, fetcher, { revalidateOnFocus: false });

  useEffect(() => {
    document.title = "Carta dell'Osteria · Osteria Aterrana";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    meta.id = "carta-robots";
    document.getElementById("carta-robots")?.remove();
    document.head.appendChild(meta);
    return () => document.getElementById("carta-robots")?.remove();
  }, []);

  if (isLoading) {
    return <p className="p-10 text-sm text-foreground/55">Carichiamo la carta…</p>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-cream px-6 py-24 text-center" data-testid="carta-non-trovata">
        <h1 className="font-serif text-3xl text-wood">Pagina non disponibile</h1>
        <p className="mt-4 text-sm text-foreground/65">
          Questo indirizzo non è più valido. Chiedi in sala il codice aggiornato.
        </p>
      </div>
    );
  }

  const categorie = (data.categories || []).filter((c) => c.items?.length);

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="carta-qr">
      <header className="border-b border-wood/10 px-6 py-9 text-center">
        <p className="eyebrow text-gold">Osteria Aterrana</p>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-wood">La nostra carta</h1>
        <p className="mt-3 text-sm text-foreground/60">
          {settings.menu_intro || "Cucina tradizionale irpina e campana."}
        </p>
      </header>

      <main className="mx-auto max-w-2xl px-6">
        {categorie.map((cat) => (
          <section key={cat.id} className="mt-12" data-testid={`carta-categoria-${cat.id}`}>
            <h2 className="font-serif text-2xl text-wood">{cat.name}</h2>
            {cat.description && (
              <p className="mt-1 text-sm text-foreground/60">{cat.description}</p>
            )}
            <ul className="mt-6 space-y-7">
              {cat.items.map((item) => (
                <li key={item.id}>
                  <div className="flex items-baseline gap-3">
                    <h3 className="font-serif text-lg text-wood">{item.name}</h3>
                    <span className="flex-1 border-b border-dotted border-wood/25" aria-hidden="true" />
                    <Prezzo item={item} />
                  </div>
                  {item.description && (
                    <p className="mt-1.5 text-sm text-foreground/65 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs tracking-wider text-foreground/60">
                    {item.price_note && <span>{item.price_note}</span>}
                    {item.reservation_only && <span className="text-sage">Solo su prenotazione</span>}
                    {item.allergens?.length > 0 && <span>Allergeni: {item.allergens.join(", ")}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="mt-16 border-t border-wood/10 pt-6 text-xs leading-relaxed text-foreground/55">
          {settings.allergen_note}
        </p>
      </main>
    </div>
  );
}
